import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';

interface Absence {
  id: string;
  userId: string;
  type: 'VACATION' | 'SICK' | 'OTHER';
  startDate: string;
  endDate: string;
  approved: boolean;
  note: string | null;
  user?: { id: string; name: string; email: string };
}

const typeLabels: Record<string, string> = {
  VACATION: 'Urlaub',
  SICK: 'Krankheit',
  OTHER: 'Sonstiges',
};

const typeColors: Record<string, string> = {
  VACATION: 'bg-blue-50 text-blue-700 border-blue-200',
  SICK: 'bg-red-50 text-red-700 border-red-200',
  OTHER: 'bg-slate-50 text-slate-700 border-slate-200',
};

export default function AbsencesPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ userId: '', type: 'VACATION', startDate: '', endDate: '', note: '' });

  const { data: absences = [], isLoading } = useQuery<Absence[]>({
    queryKey: ['absences'],
    queryFn: () => api.get('/absences').then(r => r.data),
  });

  const { data: users = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['users'],
    queryFn: () => api.get('/tenant/users').then(r => r.data),
  });

  const save = useMutation({
    mutationFn: (data: any) =>
      editId ? api.put(`/absences/${editId}`, data) : api.post('/absences', data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['absences'] }); closeForm(); },
  });

  const approve = useMutation({
    mutationFn: (id: string) => api.put(`/absences/${id}/approve`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['absences'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/absences/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['absences'] }),
  });

  function openNew() {
    setForm({ userId: '', type: 'VACATION', startDate: '', endDate: '', note: '' });
    setEditId(null);
    setShowForm(true);
  }

  function openEdit(a: Absence) {
    setForm({
      userId: a.userId,
      type: a.type,
      startDate: a.startDate.slice(0, 10),
      endDate: a.endDate.slice(0, 10),
      note: a.note || '',
    });
    setEditId(a.id);
    setShowForm(true);
  }

  function closeForm() { setShowForm(false); setEditId(null); }

  function getDays(start: string, end: string) {
    const diff = new Date(end).getTime() - new Date(start).getTime();
    return Math.max(1, Math.ceil(diff / 86400000) + 1);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Abwesenheiten</h1>
          <p className="text-sm text-slate-500 mt-1">Urlaub, Krankheit und sonstige Abwesenheiten verwalten</p>
        </div>
        <button onClick={openNew} className="sp-btn-primary">+ Abwesenheit eintragen</button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="sp-card p-6 mb-6 animate-scale-in">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">
            {editId ? 'Abwesenheit bearbeiten' : 'Neue Abwesenheit'}
          </h3>
          <form onSubmit={(e) => { e.preventDefault(); save.mutate(form); }} className="grid grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Mitarbeiter</label>
              <select className="sp-input" value={form.userId} onChange={e => setForm({ ...form, userId: e.target.value })} required>
                <option value="">Bitte wählen...</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <label className="sp-label">Art</label>
              <select className="sp-input" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                <option value="VACATION">Urlaub</option>
                <option value="SICK">Krankheit</option>
                <option value="OTHER">Sonstiges</option>
              </select>
            </div>
            <div>
              <label className="sp-label">Von</label>
              <input type="date" className="sp-input" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} required />
            </div>
            <div>
              <label className="sp-label">Bis</label>
              <input type="date" className="sp-input" value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} required />
            </div>
            <div className="col-span-2">
              <label className="sp-label">Notiz</label>
              <input type="text" className="sp-input" placeholder="Optional..." value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
            </div>
            <div className="col-span-2 flex gap-3 justify-end">
              <button type="button" onClick={closeForm} className="sp-btn-secondary">Abbrechen</button>
              <button type="submit" className="sp-btn-primary" disabled={save.isPending}>
                {save.isPending ? 'Speichern...' : editId ? 'Aktualisieren' : 'Eintragen'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="text-center py-12 text-slate-400">Laden...</div>
      ) : absences.length === 0 ? (
        <div className="sp-card p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-50 to-brand-100 flex items-center justify-center mx-auto mb-3">
            <svg className="w-7 h-7 text-brand-500" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /><path d="M12 14l-2 2m0 0l-2-2m2 2V10" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-slate-700 mb-1">Keine Abwesenheiten</h3>
          <p className="text-sm text-slate-500">Trage Urlaub oder Krankheit für deine Mitarbeiter ein.</p>
        </div>
      ) : (
        <div className="sp-card overflow-hidden">
          <table className="sp-table">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Mitarbeiter</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Art</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Zeitraum</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Tage</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Notiz</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {absences.map(a => (
                <tr key={a.id} className="border-t border-surface-100 hover:bg-surface-50 cursor-pointer" onClick={() => openEdit(a)}>
                  <td className="px-4 py-3 font-medium text-slate-800">{a.user?.name || '–'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border ${typeColors[a.type]}`}>
                      {typeLabels[a.type]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {new Date(a.startDate).toLocaleDateString('de-DE')} – {new Date(a.endDate).toLocaleDateString('de-DE')}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{getDays(a.startDate, a.endDate)}</td>
                  <td className="px-4 py-3">
                    {a.approved ? (
                      <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">Genehmigt</span>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); approve.mutate(a.id); }}
                        className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-yellow-50 text-yellow-700 border border-yellow-200 hover:bg-yellow-100 transition-colors"
                      >
                        Ausstehend
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-sm">{a.note || '–'}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); if (confirm('Abwesenheit löschen?')) remove.mutate(a.id); }}
                      className="sp-btn-icon !w-8 !h-8 text-slate-400 hover:text-red-500"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
